import { create } from 'zustand'
import type { QueryClient } from '@tanstack/react-query'
import { authApi, registerAuthAccessors } from '../services/api'
import { clearAllWorkspaceTokens } from '../services/workspaceJwt'

interface Admin {
  id: string
  email: string
  name: string
  role: 'SUPER_ADMIN' | 'ADMIN' | 'SUPPORT' | 'BILLING'
  mfaEnabled?: boolean
}

interface LoginResult {
  requiresMfa?: boolean
}

interface AuthState {
  admin: Admin | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<LoginResult | void>
  loginWithMfa: (
    email: string,
    password: string,
    mfaCode: string
  ) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

// QueryClient ref registered by main.tsx so logout can clear workspace
// tokens without importing React Query into this store directly.
let _queryClientRef: QueryClient | null = null
export function registerAuthStoreQueryClient(qc: QueryClient): void {
  _queryClientRef = qc
}

export const useAuthStore = create<AuthState>((set, get) => ({
  admin: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await authApi.login(email, password)

      // Check if MFA is required
      if (response.requiresMfa) {
        set({ isLoading: false })
        return { requiresMfa: true }
      }

      // Access token kept in memory only (not localStorage)
      // Refresh token is now in an httpOnly cookie
      set({
        admin: response.admin,
        accessToken: response.accessToken,
        isAuthenticated: true,
        isLoading: false,
      })
      return
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Login failed',
        isLoading: false,
      })
      throw error
    }
  },

  loginWithMfa: async (email: string, password: string, mfaCode: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await authApi.loginWithMfa(email, password, mfaCode)
      // Access token kept in memory only
      set({
        admin: response.admin,
        accessToken: response.accessToken,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Login failed',
        isLoading: false,
      })
      throw error
    }
  },

  logout: async () => {
    try {
      await authApi.logout()
    } catch {
      // Ignore logout errors
    } finally {
      // Drop every workspace-scoped JWT alongside the admin session so a
      // re-login doesn't carry forward a stale tenant token.
      const qc = _queryClientRef
      if (qc) clearAllWorkspaceTokens(qc)
      set({ admin: null, accessToken: null, isAuthenticated: false })
    }
  },

  checkAuth: async () => {
    // If we have an access token in memory, try to validate it
    const { accessToken } = get()
    if (!accessToken) {
      // Try to refresh using httpOnly cookie
      try {
        const refreshResponse = await authApi.refresh()
        const newAccessToken = refreshResponse.accessToken
        const admin = await authApi.meWithToken(newAccessToken)
        set({
          admin,
          accessToken: newAccessToken,
          isAuthenticated: true,
          isLoading: false,
        })
      } catch {
        set({ isLoading: false, isAuthenticated: false })
      }
      return
    }

    try {
      const admin = await authApi.me()
      set({ admin, isAuthenticated: true, isLoading: false })
    } catch {
      // Access token expired, try refresh
      try {
        const refreshResponse = await authApi.refresh()
        const newAccessToken = refreshResponse.accessToken
        set({ accessToken: newAccessToken })
        const admin = await authApi.meWithToken(newAccessToken)
        set({ admin, isAuthenticated: true, isLoading: false })
      } catch {
        set({ accessToken: null, isLoading: false, isAuthenticated: false })
      }
    }
  },
}))

// Register auth accessors so the API interceptor can read/update token without circular imports
registerAuthAccessors(
  () => useAuthStore.getState().accessToken,
  (state) => useAuthStore.setState(state)
)

// Check auth on store initialization
useAuthStore.getState().checkAuth()
